import { ChangeEvent, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { applyPricebookDraft, readPricebookDraftRefusal } from "../../../../services";
import { pushGlobalSuccess } from "../../../../success/globalSuccess";
import {
  PricebookApplyMode,
  PricebookDraftDiff,
} from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import { todayInBangkok } from "../../../../utils/date";
import ConfirmModal from "../../../ConfirmModal";
import { formatDiscountPercent } from "../constants";
import ApplyTimingFields from "./ApplyTimingFields";
import { BookDiscountChangeLine, ChangeCountChips } from "./ChangeSummary";
import PriceChangeList from "./PriceChangeList";
import {
  APPLY_CONFIRM_LABEL,
  ApplyTimingErrors,
  ApplyTimingForm,
  INITIAL_APPLY_TIMING,
  buildApplyImpactMessage,
  buildApplySuccessMessage,
  buildApplyTiming,
  describeApplyTiming,
  validateApplyTiming,
} from "./applyTiming";
import { buildDraftChangeRows } from "./priceChangeRows";
import { PricebookDraftDiffState } from "./usePricebookDraftDiff";

const NO_LINE_CHANGES = "ราคาสินค้าไม่ต่างจากต้นฉบับ";
const APPLY_FAILED = "นำร่างไปใช้ไม่สำเร็จ กรุณาลองอีกครั้ง";

interface ApplyDraftDialogProps {
  open: boolean;
  draftUuid: string;
  draftCode: string;
  /** The book the draft is applied onto; its code is what the reader types to confirm. */
  sourceCode: string;
  /** The page's comparison; it is read again when the dialog opens. */
  diffState: PricebookDraftDiffState;
  onClose: () => void;
  /** The draft is gone; its source carries the prices now, on the scheduled day or as periods. */
  onApplied: (sourceUuid: string, mode: PricebookApplyMode) => void;
}

/**
 * The last look before a draft reaches its source: what changes, who it reaches, when it takes
 * effect (now, from a later day, or only between two days), a "tested" tick, and the source's code
 * typed exactly — the same type-to-confirm the catalogue deletes use.
 */
export default function ApplyDraftDialog({
  open,
  draftUuid,
  draftCode,
  sourceCode,
  diffState,
  onClose,
  onApplied,
}: ApplyDraftDialogProps) {
  const [timing, setTiming] = useState<ApplyTimingForm>(INITIAL_APPLY_TIMING);
  const [timingErrors, setTimingErrors] = useState<ApplyTimingErrors>({});
  const [isTested, setIsTested] = useState<boolean>(false);
  const [typedCode, setTypedCode] = useState<string>("");
  const [applying, setApplying] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // A timing, a tick or a code entered for one apply must not carry over to the next.
  useEffect(() => {
    if (!open) return;
    setTiming(INITIAL_APPLY_TIMING);
    setTimingErrors({});
    setIsTested(false);
    setTypedCode("");
    setErrorMessage("");
  }, [open]);

  const { diff, loading, reload } = diffState;
  const changeRows = useMemo(
    () => (diff ? buildDraftChangeRows(diff.items) : []),
    [diff],
  );
  const sourceLabel = [sourceCode, diff?.source.name].filter(Boolean).join(" ");
  const title = `นำร่าง ${draftCode} ไปใช้กับ ${sourceLabel}`;
  const isReady = Boolean(diff) && !loading && !diff?.source_changed;
  const canApply = isReady && isTested && typedCode === sourceCode;
  const rangeBlockReason = diff && !diff.can_apply_range ? diff.range_block_reason : null;

  const handleChangeTiming = (changes: Partial<ApplyTimingForm>) => {
    setTiming((prev) => ({ ...prev, ...changes }));
    setTimingErrors({});
  };

  const handleChangeTested = (event: ChangeEvent<HTMLInputElement>) => {
    setIsTested(event.target.checked);
  };

  const handleChangeTypedCode = (event: ChangeEvent<HTMLInputElement>) => {
    setTypedCode(event.target.value);
  };

  const handleClose = () => {
    if (applying) return;
    onClose();
  };

  const handleApply = async () => {
    const found = validateApplyTiming(timing, todayInBangkok(), rangeBlockReason);
    setTimingErrors(found);
    const body = buildApplyTiming(timing);
    if (Object.keys(found).length > 0 || !body) return;
    setApplying(true);
    setErrorMessage("");
    try {
      const {
        data: { data },
      } = await applyPricebookDraft(draftUuid, { ...body, confirm_code: typedCode });
      // The API refuses a code other than the source's, so on success it names the source.
      pushGlobalSuccess(buildApplySuccessMessage(typedCode, body));
      if (data) onApplied(data.uuid, body.mode);
    } catch (error) {
      const refusal = readPricebookDraftRefusal(error);
      setErrorMessage(refusal?.message ?? APPLY_FAILED);
      // The source moved or was deleted since the comparison was read; reading it again shows why.
      if (refusal?.isConflict) reload();
    } finally {
      setApplying(false);
    }
  };

  return (
    <ConfirmModal
      open={open}
      title={title}
      showCloseButton
      confirmLabel={APPLY_CONFIRM_LABEL[timing.mode]}
      confirmDisabled={!canApply}
      loading={applying}
      onClose={handleClose}
      onConfirm={handleApply}
    >
      <Stack gap={2}>
        {loading && (
          <Typography variant="body2" color="text.secondary">
            กำลังอ่านความต่างจากสมุดราคาต้นฉบับ...
          </Typography>
        )}
        {diffState.errorMessage && (
          <Alert severity="error">{diffState.errorMessage}</Alert>
        )}
        {diff && <DiffSummary diff={diff} timing={timing} />}
        {diff && <PriceChangeList rows={changeRows} emptyText={NO_LINE_CHANGES} />}
        <ApplyTimingFields
          form={timing}
          errors={timingErrors}
          rangeBlockReason={rangeBlockReason}
          disabled={!isReady || applying}
          onChange={handleChangeTiming}
        />
        <FormControlLabel
          control={<Checkbox checked={isTested} onChange={handleChangeTested} />}
          label="ทดสอบแล้ว: ออกบิลกับร้านทดสอบของ 00-HO และราคาถูกต้อง"
        />
        <TextField
          size="small"
          fullWidth
          label={`พิมพ์ ${sourceCode} เพื่อยืนยัน`}
          value={typedCode}
          disabled={!isReady}
          onChange={handleChangeTypedCode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </Stack>
    </ConfirmModal>
  );
}

interface DiffSummaryProps {
  diff: PricebookDraftDiff;
  timing: ApplyTimingForm;
}

/** Status chips, who the apply reaches and from when, and the book % when it changes. */
function DiffSummary({ diff, timing }: DiffSummaryProps) {
  const hasTestShops = diff.draft_shop_count > 0;
  const testShopLabel = hasTestShops
    ? `ผูกร้านทดสอบ ${numberWithCommas(diff.draft_shop_count)} ร้าน`
    : "ยังไม่ได้ผูกร้านทดสอบ";
  const sourceStatusLabel = diff.source_changed
    ? "ต้นฉบับถูกแก้หลังทำสำเนา ต้องทำสำเนาใหม่"
    : "ต้นฉบับไม่ถูกแก้หลังทำสำเนา";
  // A range writes periods priced at the draft's %, so the source keeps its own book %.
  const isRange = timing.mode === "range";
  const showsKeptDiscount =
    isRange && diff.discount_percent.old !== diff.discount_percent.new;
  const keptDiscountText = `ส่วนลดทั้งสมุดของ ${diff.source.code} คงเดิม ${formatDiscountPercent(
    diff.discount_percent.old,
  )} ราคาในช่วงคิดตามร่าง`;

  return (
    <Stack gap={1}>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Chip
          size="small"
          color={diff.source_changed ? "error" : "success"}
          label={sourceStatusLabel}
        />
        <Chip
          size="small"
          color={hasTestShops ? "success" : "warning"}
          label={testShopLabel}
        />
        <Chip size="small" color="info" label={describeApplyTiming(timing)} />
        <ChangeCountChips summary={diff.summary} />
      </Stack>
      <Typography variant="body2">{buildApplyImpactMessage(diff, timing)}</Typography>
      {showsKeptDiscount && (
        <Typography variant="body2">{keptDiscountText}</Typography>
      )}
      {!isRange && <BookDiscountChangeLine change={diff.discount_percent} />}
    </Stack>
  );
}
